<template>
  <div class="mb-3 tz-table-container">
    <div class="mb-3">
      <!-- <language-btn/> -->
    </div>
    <div v-if="loading">
      <b-spinner
        class="tz-table-loading"
        variant="light"
      />
      <p>
        (If there is no response for a long time, please clear the browser cache and force refresh the page) 
      </p>
    </div>
    <b-table
      v-else
      hover
      dark
      class="main-table"
      :fields="fields"
      :items="items"
    >
      <template #cell(zone)="data">
        <div
          class="cell-container"
          @mouseover="data.value.showSubscription = true"
          @mouseleave="data.value.showSubscription = false"
        >
          <div
            v-for="(name, index) in data.value.nameList"
            :key='index'
          >
            <span v-if="showExpTier && data.value.tier" class="legend-icon tier tier-exp mx-1">{{ data.value.tier.exp }}</span>
            <span v-if="showLootTier && data.value.tier" class="legend-icon tier tier-loot mx-1">{{ data.value.tier.loot }}</span>
            {{ name[language] }}
            <b-badge
              v-if="data.value.showNowBadge"
              class="badge-pill badge-success"
            >
              Now
            </b-badge>
            <b-badge
              v-if="data.value.showComingBadge"
              class="badge-pill badge-danger"
            >
              Coming soon
            </b-badge>
            <b-icon 
              v-if="serviceWorker && subscribedTZs.includes(data.value.id)"
              icon="bell-fill"
              class="rounded px-2 pointer"
              variant="warning"
              @click="unsubscribeTerrorZone(data.value.id)"
            />
            <b-icon 
              v-else-if="serviceWorker && data.value.showSubscription"
              icon="bell-fill"
              class="rounded px-2 pointer"
              variant="secondary"
              @click="subscribeTerrorZone(data.value.id)"
            />
          </div>
          <Immunities
            v-if="showImmunities"
            :id='data.value.timestamp.toString()'
            :immunities="data.value.immunities"
            :areas="data.value.areas"
            :language="language"
          />
        </div>
      </template>
    </b-table>
    <NotificationTipModal
      ref='notification-tip-modal'
      :language="language"
      :tzId="currentsubscribingTZ"
      @show-install-guide="$emit('show-install-guide')"
    />
  </div>
</template>

<script>
import { tzData } from '@/utils/tz_data'
import { getDataFromAllSites, getData, getUser, getRegionByUrl, getRegion, getPublicKey, subscribeTZ } from '@/utils/api'
import Immunities from './Immunities.vue'
// import LanguageBtn from './LanguageBtn.vue'
import NotificationTipModal from './modals/NotificationTip.vue'

export default {
  name: 'TerrorZoneQueryTable',

  components: {
    Immunities,
    // LanguageBtn,
    NotificationTipModal
  },

  data() {
    return {
      fields: [
        { key: 'time', thStyle: { width: "35%" } },
        { key: 'zone', label: 'Terror Zone', thStyle: { width: "65%" } },
      ],
      items: [],
      loading: false,
      maxTS: 0,
      nowTzTS: 0,
      selectedDate: null,
      subscribedTZs: [],
      currentsubscribingTZ: null,
    }
  },

  emits: ['show-install-guide'],

  props: {
    language: {
      type: String,
      required: true
    },
    playMode: {
      type: String,
      required: true
    },
    serviceWorker: {
      type: Object,
      default: null,
    },
    selectedTimestamp: {
      type: Number,
      default: null,
    },
    turnstileToken: {
      type: String,
      default: null,
    },
    showImmunities: {
      type: Boolean,
      default: false,
    },
    showExpTier: {
      type: Boolean,
      default: false,
    },
    showLootTier: {
      type: Boolean,
      default: false,
    },
  },

  created() {
    this.subscribedTZs = localStorage.getItem('subscribed_tz');
    if (this.subscribedTZs) {
      this.subscribedTZs = JSON.parse(this.subscribedTZs);
    } else {
      this.subscribedTZs = [];
    }
    this.fetch();
  },

  watch: {
    turnstileToken(newToken) {
      if (newToken) {
        this.refetch();
      }
    },
    selectedTimestamp() {
      this.refetch();
    },
    serviceWorker (newSW, oldSW) {
      if (!oldSW && newSW) {
        if (this.subscribedTZs?.length) {
          getUser().then(this.processSubscriptionData);
        } else if (!localStorage.getItem('server_public_key')) {
          getPublicKey().then(this.processSubscriptionData);
        }
      }
    }
  },
  
  methods: {
    fetch () {
      this.loading = true;
      let start = null, end = null;
      if (this.selectedTimestamp) {
        start = parseInt(this.selectedTimestamp / 1000);
        end = start + 24 * 60 * 60 - 1;
      }
      const region = getRegion();
      if (region) {
        getData(this.playMode, start, end, region, this.turnstileToken)?.then(this.initTerrorZone)?.catch(this.handle_errors);
      } else {
        getDataFromAllSites(this.playMode, start, end, this.turnstileToken)?.forEach(resp => resp?.then(this.initTerrorZone))?.catch(this.handle_errors);
      }
    },

    refetch () {
      this.maxTS = 0;
      this.nowTzTS = 0;
      this.fetch();
    },

    initTerrorZone (data) {
      if (!data) {
        console.log('Failed to get data.');
        return;
      }
      const isOldData = !data['data'].find(x => x['time'] * 1000 > this.maxTS);
      if (isOldData) {
        console.log('Skip update since already got the data.');
        return;
      }
      this.items = data['data'].map(this.dataRender).filter(x => x?.zone?.nameList?.length > 0);
      this.loading = false;
      let region = getRegion();
      if (!region) {
        region = getRegionByUrl(data['url']);
        localStorage.setItem('region', region);
        localStorage.setItem('fetch_all_ts', Date.now());
      }
    },

    dataRender (item) {
      let nameList = [];
      let immunities = [];
      let tier = null;
      let areas = [];
      const id = item['zone'];
      if (Array.isArray(item['zone'])) {
        for (let zoneId in item['zone']) {
          const tzItem = tzData.find(x => x['id'] === zoneId);
          if (!tzItem) {
            continue;
          }
          nameList.push(tzItem['name']);
        }
      } else {
        const tzItem = tzData.find(x => x['id'] === item['zone']);
        if (!tzItem) {
          return null;
        }
        nameList = [tzItem['name']];
        immunities = tzItem['immunities'];
        tier = {
          'exp': tzItem['tier-exp'],
          'loot': tzItem['tier-loot'],
        };
        areas = tzItem['areas'];
      }
      const ts = parseInt(item['time'] / 60) * 60 * 1000;
      if (ts > this.maxTS) {
        this.maxTS = ts;
      }
      const timeNow = new Date();
      const timeTZ = new Date(ts);
      let tzDuration = 60 * 60 * 1000;
      /*
      const specialEventStart = new Date(1735239600 * 1000);
      const specialEventEnd = new Date(1735326000 * 1000);
      if (timeNow < specialEventEnd && timeTZ >= specialEventStart && timeTZ < specialEventEnd) {
        tzDuration = 15 * 60 * 1000;
      }
      */
      let showNowBadge = timeNow.getTime() - ts < tzDuration;
      const showComingBadge = timeNow < timeTZ;
      if (showComingBadge) {
        showNowBadge = false;
      }
      if (showNowBadge && this.nowTzTS > ts) {
        showNowBadge = false;
      }
      if (showNowBadge) {
        this.nowTzTS = ts;
      }
      return {
        'time': timeTZ.toLocaleString(),
        'zone': {
          'id': id,
          'nameList': nameList,
          'immunities': immunities,
          'tier': tier,
          'areas': areas,
          'showNowBadge': showNowBadge,
          'showComingBadge': showComingBadge,
          'timestamp': ts,
          'showSubscription': false,
        }
      };
    },

    handle_errors (errors) {
      if (errors === 'unauthorized request') {
        const errorMessage = 'Authentication failed. Unable to process request. Please refresh the page.';
        this.loading = false;
        this.$emit('error', errorMessage);
      } else {
        console.log(errors)
      }
    },

    processSubscriptionData (data) {
      if (!data) {
        return;
      }
      if (data['subscribed_tz']) {
        this.subscribedTZs = data['subscribed_tz'];
        localStorage.setItem('subscribed_tz', JSON.stringify(this.subscribedTZs));
      }
      if (data['public_key']) {
        localStorage.setItem('server_public_key', data['public_key']);
      }
    },

    async subscribeTerrorZone (tzId) {
      if (!this.serviceWorker) {
        return;
      }
      console.log('Subscribe Terror Zone: ', tzId);
      await subscribeTZ(this.serviceWorker, tzId, true);
      this.subscribedTZs.push(tzId);
      localStorage.setItem('subscribed_tz', JSON.stringify(this.subscribedTZs));
      this.currentsubscribingTZ = tzId;
      if (!localStorage.getItem('do_not_show_notification_modal')) {
        this.$refs['notification-tip-modal'].show();
      }
    },

    async unsubscribeTerrorZone (tzId) {
      if (!this.serviceWorker) {
        return;
      }
      console.log('Unsubscribe Terror Zone: ', tzId);
      subscribeTZ(this.serviceWorker, tzId, false);
      const index = this.subscribedTZs.indexOf(tzId);
      if (index >= 0) {
        this.subscribedTZs.splice(index, 1);
      }
      localStorage.setItem('subscribed_tz', JSON.stringify(this.subscribedTZs));
    },
  },
}
</script>

<style scoped>
.tz-table-loading {
  margin: 10vh;
}
.main-table {
  width: 75%;
  max-width: 900px;
  margin: auto;
}
.main-table th {
  color: #fff;
  background: #333;
}
.cell-container {
  width: 100%;
}
@media (max-width: 600px) {
  .main-table {
    width: 90%;
  }
}
.table-cell {
  background-color: #777;
  color: #fff;
}
.badge-success {
  color: #fff;
  background-color: #28a745;
}
.badge-pill {
  padding-right: 0.6em;
  padding-left: 0.6em;
  border-radius: 10rem;
}
.badge-danger {
  color: #fff;
  background-color: #dc3545;
}
.tz-text {
  color: #ffbe33
}
</style>
